import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaImage,
  FaRobot,
  FaTags,
  FaRegCommentDots,
  FaInstagram,
  FaExclamationTriangle,
} from "react-icons/fa";

import companyAutomationService from "../../services/companyAutomationService";
import UploadPostStep from "./steps/UploadPostStep";
import AutomationTypeStep from "./steps/AutomationTypeStep";
import KeywordsStep from "./steps/KeywordsStep";
import MessageStep from "./steps/MessageStep";

const COMPANY_DM_BASE = "/company/dashboard/dm-automation";

const STEPS = [
  { id: 1, name: "Upload Post", Icon: FaImage },
  { id: 2, name: "Type of Automation", Icon: FaRobot },
  { id: 3, name: "Keywords", Icon: FaTags },
  { id: 4, name: "Message", Icon: FaRegCommentDots },
];

const EMPTY_FOLLOWER_GATE = {
  enabled: false,
  accessPromptMessage: "",
  accessButtonTitle: "",
  followPromptMessage: "",
  visitProfileButtonTitle: "",
  followingButtonTitle: "",
  profileUrl: "",
};

const EMPTY_DRAFT = {
  uploadPost: null,
  typeOfAutomation: { type: "DM" },
  keywords: [],
  productLink: "",
  message: "",
  sendReplyToCommentFirst: false,
  replyCommentMessage: "",
  followerGate: EMPTY_FOLLOWER_GATE,
};

/**
 * InstagramAutomation
 * ------------------------------------------------------------------
 * Multi-step wizard for creating / editing a brand DM automation.
 *
 *   Step 1 — Upload Post (post URL + title/description)
 *   Step 2 — Type of Automation (DM only today)
 *   Step 3 — Keywords (+ optional product link)
 *   Step 4 — Message (DM body + placeholders + optional reply toggle)
 *
 * Edit mode is triggered by the presence of a route param `:id`. We
 * eagerly preload the automation document so the form lands with
 * the user's existing values.
 *
 * Connection check: before letting the brand build an automation, we
 * call `getStatus()` to ensure their Instagram account is connected.
 * If not, we render a guided CTA instead of the wizard.
 */
const InstagramAutomation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [activeStep, setActiveStep] = useState(1);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [automationLoading, setAutomationLoading] = useState(isEditMode);

  const fetchStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const response = await companyAutomationService.getStatus();
      setStatus(response?.data || null);
    } catch (err) {
      console.error("Failed to fetch automation status", err);
      toast.error(err.message || "Failed to check Instagram connection");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadAutomation = useCallback(async () => {
    if (!isEditMode) return;
    try {
      setAutomationLoading(true);
      const response = await companyAutomationService.getAutomationById(id);
      const automation = response?.data;
      if (!automation) {
        toast.error("Automation not found");
        navigate(COMPANY_DM_BASE);
        return;
      }
      setDraft({
        uploadPost: automation.uploadPost
          ? {
              title: automation.uploadPost.title || "",
              description: automation.uploadPost.description || "",
              mediaLink: automation.uploadPost.mediaLink || "",
            }
          : null,
        typeOfAutomation: automation.typeOfAutomation || { type: "DM" },
        keywords: automation.keywords || [],
        productLink: automation.productLink || "",
        message: automation.message || "",
        sendReplyToCommentFirst: !!automation.sendReplyToCommentFirst,
        replyCommentMessage: automation.replyCommentMessage || "",
        followerGate: {
          ...EMPTY_FOLLOWER_GATE,
          ...(automation.followerGate || {}),
          enabled: !!automation.followerGate?.enabled,
        },
      });
    } catch (err) {
      console.error("Failed to load automation", err);
      toast.error(err.message || "Failed to load automation");
      navigate(COMPANY_DM_BASE);
    } finally {
      setAutomationLoading(false);
    }
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    loadAutomation();
  }, [loadAutomation]);

  const instagramConnected = Boolean(status?.instagram?.connected);

  const stepData = useMemo(
    () => ({
      1: draft.uploadPost,
      2: draft.typeOfAutomation,
      3: { keywords: draft.keywords, productLink: draft.productLink },
      4: {
        message: draft.message,
        productLink: draft.productLink,
        sendReplyToCommentFirst: draft.sendReplyToCommentFirst,
        replyCommentMessage: draft.replyCommentMessage,
        followerGate: draft.followerGate,
      },
    }),
    [draft]
  );

  const handleNext = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setActiveStep((s) => Math.min(STEPS.length, s + 1));
  };

  const handleBack = () => {
    setActiveStep((s) => Math.max(1, s - 1));
  };

  const handleStepClick = (stepId) => {
    if (stepId <= activeStep) setActiveStep(stepId);
  };

  const buildPayload = (finalPatch) => ({
    uploadPost: draft.uploadPost,
    typeOfAutomation: draft.typeOfAutomation,
    keywords: draft.keywords,
    productLink: draft.productLink,
    message: finalPatch.message,
    sendReplyToCommentFirst: finalPatch.sendReplyToCommentFirst,
    replyCommentMessage: finalPatch.replyCommentMessage,
    followerGate: finalPatch.followerGate,
  });

  const handleSubmit = async (finalPatch) => {
    if (!draft.uploadPost?.title || !draft.uploadPost?.mediaLink) {
      toast.error("Please complete the Upload Post step");
      setActiveStep(1);
      return;
    }
    if (!draft.typeOfAutomation?.type) {
      toast.error("Please pick an automation type");
      setActiveStep(2);
      return;
    }
    if (!draft.keywords || draft.keywords.length === 0) {
      toast.error("Please add at least one keyword");
      setActiveStep(3);
      return;
    }

    const payload = buildPayload(finalPatch);

    try {
      setIsSubmitting(true);
      const response = isEditMode
        ? await companyAutomationService.updateAutomation(id, payload)
        : await companyAutomationService.createAutomation(payload);

      if (response?.success) {
        toast.success(
          isEditMode
            ? "Automation updated successfully"
            : "Automation created successfully"
        );
        navigate(COMPANY_DM_BASE);
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Submit automation failed", err);
      if (err.code === "INSTAGRAM_NOT_CONNECTED") {
        toast.error("Connect Instagram before creating an automation");
        navigate("/company/dashboard/social-accounts");
        return;
      }
      toast.error(err.message || "Failed to save automation");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (statusLoading || (isEditMode && automationLoading)) {
    return (
      <div className="bg-[#F4F2EF] h-full w-full">
        <div className="w-full rounded-2xl border border-orange-50 bg-white/70 p-6 text-sm text-gray-500 shadow-sm">
          Loading…
        </div>
      </div>
    );
  }

  if (!instagramConnected) {
    return (
      <div className="bg-[#F4F2EF] h-full w-full">
        <div className="w-full rounded-2xl border border-orange-50 bg-white/70 p-6 shadow-sm">
          <Link
            to={COMPANY_DM_BASE}
            className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            <FaArrowLeft className="h-3 w-3" />
            Back to automations
          </Link>

          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-orange-200 bg-orange-50/40 px-6 py-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <FaInstagram className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-base font-semibold text-gray-900">
              Connect Instagram to continue
            </h2>
            <p className="mt-2 max-w-md text-xs md:text-sm text-gray-600">
              You&apos;ll need a connected Instagram Business account before
              you can create a DM automation.
            </p>
            <button
              type="button"
              onClick={() => navigate("/company/dashboard/social-accounts")}
              className="mt-6 rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
            >
              Connect Instagram
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F4F2EF] h-full w-full">
      <div className="w-full rounded-2xl border border-orange-50 bg-white/70 p-2 shadow-sm md:p-4">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <Link
              to={COMPANY_DM_BASE}
              className="inline-flex items-center gap-2 text-[11px] font-medium text-gray-500 hover:text-gray-700"
            >
              <FaArrowLeft className="h-3 w-3" />
              Back to automations
            </Link>
            <h1 className="mt-1 text-sm md:text-xl font-bold tracking-tight text-gray-900">
              {isEditMode ? "Edit Automation" : "New Instagram Automation"}
            </h1>
            <p className="mt-1 max-w-2xl text-[10px] md:text-xs text-gray-600">
              Connected as{" "}
              <span className="font-semibold text-gray-800">
                @{status?.instagram?.username || "your brand"}
              </span>
              .
            </p>
          </div>
        </div>

        <div className="mt-4 -mx-4 h-px bg-[#E3DFD9] sm:-mx-4 lg:-mx-6" />

        {/* Wizard body */}
        <div className="mt-6 flex flex-col gap-6 lg:flex-row">
          {/* Sidebar Steps */}
          <aside className="w-full lg:w-1/4">
            <ol className="space-y-2">
              {STEPS.map(({ id: stepId, name, Icon }) => {
                const isActive = activeStep === stepId;
                const isCompleted = stepId < activeStep;
                const clickable = stepId <= activeStep;
                return (
                  <li key={stepId}>
                    <button
                      type="button"
                      onClick={() => clickable && handleStepClick(stepId)}
                      disabled={!clickable}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition ${
                        isActive
                          ? "bg-orange-100 text-orange-700"
                          : isCompleted
                          ? "text-orange-600 hover:bg-orange-50"
                          : "text-gray-400"
                      } ${clickable ? "cursor-pointer" : "cursor-not-allowed"}`}
                    >
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${
                          isActive || isCompleted
                            ? "bg-orange-600 text-white"
                            : "border border-gray-300 bg-white text-gray-400"
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-xs md:text-sm font-medium">
                        {name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            {isEditMode && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                <FaExclamationTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>
                  Changes apply immediately to any new comments – previously
                  processed comments are not retroactively re-sent.
                </p>
              </div>
            )}
          </aside>

          {/* Main step content */}
          <section className="flex-1 rounded-2xl bg-gray-50 p-2 md:p-4">
            {activeStep === 1 && (
              <UploadPostStep data={stepData[1]} onNext={handleNext} />
            )}
            {activeStep === 2 && (
              <AutomationTypeStep
                data={stepData[2]}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {activeStep === 3 && (
              <KeywordsStep
                data={stepData[3]}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {activeStep === 4 && (
              <MessageStep
                data={stepData[4]}
                onSubmit={handleSubmit}
                onBack={handleBack}
                isSubmitting={isSubmitting}
                isEditMode={isEditMode}
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default InstagramAutomation;
